import * as THREE from 'three';
import { BeatSequencer } from '@/story/sequencer';
import { WEAKEST_LATCH, LATCH_CUES, LATCH_FLAGS, LATCH_IDS } from '@/story/act1/weakestLatch';
import { HALL, WALKWAY_DOOR_OUT_Z, walkwayDoorLeaf, walkwayDoorLeafTouches } from '@/world/farrowing';
import { ROSALIE_TUNING } from '@/physics/character';
import { DoorSwing } from '@/world/doorSwing';
import { FAR_TROUGH_WAYPOINT, latchWatcher, latchWorkerRoute } from '@/world/latchScene';
import { bakeFarrowingLightGrid, farrowingCover } from '@/world/farrowingStealth';
import { LatchDirector, type LatchDebug } from '@/world/latchDirector';
import { penGateLeaf, PEN_GATE } from '@/models/env/pen-gate';
import { penGateHinge, takeLivePenGate } from '@/world/livePenGate';
import { Worker, type WorkerState } from '@/systems/worker';
import { StealthSystem } from '@/systems/stealth';
import { FootfallStride, heardGait } from '@/systems/footfallStride';
import type { Interactable } from '@/systems/interaction';
import type { HallSolid } from '@/world/solids';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

/**
 * How close to her a ray may stop and still count as reaching her.
 *
 * Beat 2's own policy, and the only one in the game that forgives: the shared
 * `lineOfSight` treats any hit short of her as blocked. The ray ends ON her
 * back, and in the pen that back is pressed into straw-level solids the shared
 * rule would call a wall.
 */
const SIGHT_FORGIVENESS = 0.02;

/** Past the kerb: the same arithmetic as `main.ts`'s `insidePen`, which the camera and the foley read. */
const insidePen = (x: number): boolean => Math.abs(x) > HALL.walkway + 0.12;

/**
 * Act 1 beat 2, *The Weakest Latch*: one worker on a round, one gate that
 * gives.
 *
 * Until session 85 this was a branch in `main.ts` plus lines in eight other
 * places there (D-232). Where each piece now runs, against where it ran:
 *
 * - The worker's step, his `observe` target, the gate's swing and its
 *   collider, his watcher and her footfalls ran right after the controller;
 *   they still do, in `afterMove`, so the interaction system samples where he
 *   is this tick. Her footfalls still accumulate `speed * dt`
 *   (`tick.motion.speed`), not the displacement.
 * - The three flags ran there too; they now run in `fixedStep`, after
 *   interaction events, still before `story.update` in the same tick. Each
 *   reads state `afterMove` already settled this tick, so none can change.
 * - The latch's root (release) and nudge (rattle) ran in the interaction-event
 *   loop; `fixedStep` reads them as this tick's edges.
 * - The stealth query ran after `physics.step`, for the reason `main.ts`
 *   gives there (Rapier's query structure); it is `afterPhysics` now.
 * - The worker's mesh reads his state on the render step, with the boot
 *   landings counted since the last frame, as before.
 */
function createWeakestLatchPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const world = capabilities.world;
  if (world === undefined) throw new Error('beat 2 / part 1: the host offered no world capability');
  const farrowing = world.room.farrowing;
  if (farrowing === undefined) throw new Error('beat 2 / part 1: the room offers no farrowing seam — booted outside the farrowing hall');
  const bay = farrowing.bay;

  // Beat 1 has already happened. Her littermate was taken, and beat 2 opening
  // with both of them in the straw would be the game forgetting the only thing
  // that has occurred so far. **The collider goes with the mesh** — booting
  // straight to `?beat=2` never runs beat 1's `onSiblingTaken`, and removing
  // only the picture left a solid piglet-shaped hole in the creep, exactly
  // where the beat asks her to walk (found on independent review; D-038).
  bay.takenSibling.removeFromParent();
  farrowing.removeTakenSiblingCollider();
  farrowing.removeShutGateCollider();

  const { hinge: gateHinge, swing: gateSwing } = penGateHinge();

  // Built in the leaf's OWN half-extents plus a yaw, unlike the shut one it
  // replaces. The two describe the same box at `open = 0` — `pen-gate.spec.ts`
  // asserts exactly that — and only this one can describe it at any other
  // angle. No `camera` field: the legacy collider had no collision groups.
  const shut = penGateLeaf(gateHinge, gateSwing, 0);
  const gateSolid: HallSolid = {
    name: 'pen-gate-live', half: shut.half, pos: shut.pos, yaw: shut.rotY, colour: 0, kind: 'metal', visible: false,
  };
  const gateCollider = world.colliders.add(gateSolid);
  const gateQuaternion = new THREE.Quaternion();
  const gateEuler = new THREE.Euler();

  // The walkway door, the same way: the hall's shut leaf out, a swinging one
  // in. He opens it to come in and leaves it ajar behind him, and the gap is
  // her way out (`WALKWAY_DOOR_AJAR`, `walkway-door.spec.ts`). Its angle is
  // simulation for the reason the gate's is: she squeezes past this collider.
  farrowing.removeWalkwayDoorCollider();
  const door = new DoorSwing(true);
  const doorCollider = world.colliders.add(walkwayDoorLeaf(0));
  const doorQuaternion = new THREE.Quaternion();

  // The gate stops being part of the merged dressing, so it can swing:
  // `takeLivePenGate` says why that has to be a swap. Beat 1 makes the same
  // one, for its worker.
  const gateMesh = takeLivePenGate(world.scene).mesh;

  // Both beats get the whole man: the bay builds the full `worker` figure for
  // beats 1 and 2 since Alex saw beat 1's cropped legs as a man with no body
  // (D-092). The `worker-legs` asset stays in the kit, gated, for a shot framed
  // below a kerb.
  const latch = new LatchDirector(
    { worker: bay.boots, doorHinge: farrowing.walkwayDoorHinge, gate: gateMesh, gateHinge, gateSwing },
    {
      // His boots are loud and hers are not, and neither of those facts is
      // audible on its own — so the one that matters (hers) is fed to the
      // stealth system in `afterMove`. Spec §5.1: he hears her, and the player
      // must be able to say afterwards which step gave her away.
      onBootfall: (at) => world.playOneShot('footfall-concrete', { gain: 0.5, position: at, detune: 70 }),
      onGateGiven: () => world.playOneShot('footfall-concrete', {
        gain: 0.7, position: { x: gateHinge.x, y: 0.3, z: gateHinge.z }, detune: -700,
      }),
    },
  );

  const route = latchWorkerRoute();
  const worker = new Worker(route);
  const watcher = latchWatcher({ x: worker.state.x, z: worker.state.z }, worker.state.facing);
  const stealth = new StealthSystem(bakeFarrowingLightGrid(), farrowingCover(), [watcher], (from, to) => {
    const distance = Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z);
    if (distance < 0.001) return true;
    const hit = world.rayHit(from, to);
    return hit === null || hit >= distance - SIGHT_FORGIVENESS;
  });
  const story = new BeatSequencer(WEAKEST_LATCH);
  const herFootfalls = new FootfallStride();

  const interactables: readonly Interactable[] = [
    {
      id: LATCH_IDS.worker,
      // Re-pointed every fixed step from his simulation position — the first
      // moving interactable in the game. See `InteractionSystem.moveTo`.
      at: { x: worker.state.x, y: 0.9, z: worker.state.z },
      // **The first route on her map, and it is drawn from the same waypoints
      // he walks.** Spec §5.3 asks for *"patrol routes as dashed lines"*; this
      // is the one thing in Act 1 that IS a patrol, and taking the line from
      // `route` rather than restating it means a route she remembers cannot
      // drift from the route he takes.
      mark: { kind: 'route', at: route.waypoints.map((waypoint) => ({ x: waypoint.x, z: waypoint.z })), glyph: 'person' },
      verbs: ['observe'],
      // 7 m, which `observeReachSlack` turns into 21. He walks the length of a
      // 20 m hall and the point of watching him is that it is done from inside
      // the pen. **It was 4, and 4 was measured wrong**: her bay is at
      // z = -8.8 and the far trough at +8.0, so a 12 m reach went dead at
      // exactly the moment the beat is named for (D-066). The cone still
      // holds — `observeConeHalfAngle` is 1.2 rad, so this is not the D-043
      // failure where a slack multiplier made a thing watchable from anywhere.
      reach: 7,
      duration: 3.2,
      enabled: false,
    },
    {
      id: LATCH_IDS.latch,
      // The drop-catch at the free end of the leaf, on the walkway side of the
      // kerb line — reachable from inside the pen, which is the only side she
      // is ever on when it matters.
      at: { x: gateHinge.x, y: 0.5, z: gateHinge.z - gateSwing * (PEN_GATE.length - 0.08) },
      verbs: ['nudge', 'root'],
      reach: 0.7,
      // Longer than beat 1's straw, and it should be: the one physical effort
      // in the beat. A two-second hold would make forcing a gate feel like
      // picking up a stick.
      duration: 3.4,
      // Live from the first frame, unlike everything in beat 1. The rattle IS
      // the tutorial: a player who touches every gate on the way past finds the
      // weak one before the beat offers it, and that discovery is the beat's
      // title. `offerLatch` turns nothing on.
    },
  ];

  /** His state as `afterMove` left it this tick; the flags read it. */
  let workerState: WorkerState = worker.state;
  /**
   * Boot landings since the last rendered frame. Latched for the render step,
   * not read from there: a one-tick flag sampled at display rate is played
   * twice on a fast machine and dropped on a slow one.
   */
  let pendingBootfalls = 0;
  let disposed = false;

  return {
    story,
    interactables,
    stealth,
    get fade(): number { return latch.fade; },
    // Beat 2 never takes the camera — it opens in gameplay because beat 1
    // earned it — so its only suppressor is its own fade (the legacy term).
    get promptSuppressed(): boolean { return !disposed && latch.fade > 0.5; },
    afterMove({ dt, player, motion }): void {
      // ── The order inside this is load-bearing ─────────────────────────────
      //
      // The worker advances FIRST, before the interaction system reads the
      // world, because the thing she can watch is him: a prompt resolved
      // against last tick's position would put the `observe` cone a step
      // behind the boots the player is aiming at. Then the gate, then his
      // watcher and her footfalls, which need both final positions. D-044:
      // anything the stealth system reads advances in the fixed step.
      workerState = worker.step(dt);
      if (workerState.footfall) pendingBootfalls += 1;
      // His knee, not the floor and not his head: she is a piglet on a walkway
      // looking at a man's legs.
      world.interaction.moveTo(LATCH_IDS.worker, { x: workerState.x, y: 0.45, z: workerState.z });

      const leaf = penGateLeaf(gateHinge, gateSwing, latch.stepGate(dt));
      gateQuaternion.setFromEuler(gateEuler.set(0, leaf.rotY, 0));
      world.colliders.setPose(gateCollider, { position: leaf.pos, quaternion: gateQuaternion });

      const moving = workerState.phase === 'walking' || workerState.phase === 'returning';
      // Held while its next pose would be in her: see `DoorSwing.step`.
      const doorLeaf = walkwayDoorLeaf(door.step(
        dt,
        { x: workerState.x, z: workerState.z, moving },
        (angle) => walkwayDoorLeafTouches(angle, player, ROSALIE_TUNING.radius),
      ));
      doorQuaternion.setFromEuler(gateEuler.set(0, doorLeaf.yaw ?? 0, 0));
      world.colliders.setPose(doorCollider, { position: doorLeaf.pos, quaternion: doorQuaternion });

      watcher.position.x = workerState.x;
      watcher.position.z = workerState.z;
      watcher.facing = workerState.facing;
      if (motion === undefined) return;
      // Her footfalls are what he can hear, per STRIDE and not per tick: the
      // first version emitted on every moving tick and took him from unaware
      // to detected in 83 ms. **Only while he is in the building** — sound goes
      // round corners but not through the end wall he is parked behind, and
      // gated on his phase because "he is off-stage" is the fact. Stealth
      // still steps while he is parked, so suspicion keeps decaying.
      const heardAs = heardGait(motion.gait);
      const inTheRoom = workerState.phase !== 'parked';
      if (herFootfalls.step(motion.speed * dt, heardAs, inTheRoom && motion.grounded) && heardAs !== null) {
        // The surface from her position, NOT from `audio.surface`, which is set
        // on the render step: D-045, simulation never reads presentation.
        stealth.emitFootfall({ x: player.x, y: player.y, z: player.z }, insidePen(player.x) ? 'straw' : 'concrete', heardAs);
      }
    },
    fixedStep({ player, raiseFlag, hasFlag, nudged, rooted }): void {
      // The hold completed: the catch comes off its keeper and the gate starts
      // to lean. `release()` is idempotent, and the swing is the only
      // confirmation the player gets — there is no HUD (spec §5.1).
      if (rooted?.(LATCH_IDS.latch) === true) latch.release();
      // A rattle, and nothing else: a loose noise a solid gate would not make,
      // which is how the player finds out this is the weak one.
      if (nudged?.(LATCH_IDS.latch) === true) {
        world.playOneShot('footfall-concrete', { gain: 0.32, position: { x: gateHinge.x, y: 0.4, z: gateHinge.z }, detune: 640 });
      }
      // The three things only the simulation can know, handed to the beat.
      if (workerState.waypoint === FAR_TROUGH_WAYPOINT && workerState.phase === 'dwelling') raiseFlag(LATCH_FLAGS.roundLearned);
      if (latch.gatePassable) raiseFlag(LATCH_FLAGS.gateOpen);
      // Through the walkway door, with the gate open. A plane only the walkway
      // door reaches, and the gate flag as well so a hole in the pen can never
      // stand in for the latch (playthrough audit row 2; `cold-spawn.spec.ts`,
      // both halves watched red on the old 0.7 m radius).
      if (hasFlag(LATCH_FLAGS.gateOpen) && player.z <= WALKWAY_DOOR_OUT_Z) raiseFlag(LATCH_FLAGS.atDoor);
    },
    afterPhysics({ dt, player }): void {
      const snapshot = stealth.step(dt, { x: player.x, y: player.y, z: player.z });
      // The whole consequence, and deliberately not a capture: he straightens
      // up and goes, and she waits for the next round. `interrupt()` is
      // idempotent, so calling it on every detected tick is harmless.
      if (snapshot.watchers.some((entry) => entry.state === 'detected')) worker.interrupt();
    },
    handleCue(cue): void {
      latch.handle(cue as Parameters<LatchDirector['handle']>[0]);
      // On a cue rather than at boot, because `settle`'s six seconds are hers.
      if (cue.id === LATCH_CUES.roundBegins) worker.start();
      if (cue.id === LATCH_CUES.offerWorker) world.interaction.setEnabled(LATCH_IDS.worker, true);
      // `offerLatch` deliberately enables nothing — see the latch's entry above.
    },
    renderStep(frameDt): void {
      if (disposed) return;
      // Presentation READS the worker; the fixed step moved him (D-044).
      latch.update(frameDt, worker.state, pendingBootfalls, door.angle);
      pendingBootfalls = 0;
    },
    diagnostics(): { readonly kind: 'latch'; readonly latch: LatchDebug } {
      const state = worker.state;
      const snapshot = stealth.snapshot();
      const seen = snapshot.watchers[0];
      const detection = snapshot.lastDetection;
      return {
        kind: 'latch',
        latch: {
          worker: { x: state.x, z: state.z, facing: state.facing, phase: state.phase, waypoint: state.waypoint },
          roundsCompleted: state.roundsCompleted,
          // Read off the scene graph, not off a flag: the assertion that matters
          // is that the littermate is not in the FRAME.
          littermateBack: bay.takenSibling.parent !== null,
          gateOpen: latch.gateOpen,
          gatePassable: latch.gatePassable,
          doorAngle: door.angle,
          suspicion: seen?.suspicion ?? 0,
          watcherState: seen?.state ?? 'unaware',
          // The LIVE visibility, not just the one at detection: this explains a
          // near miss, which is what found the phantom suspicion on the opening frame.
          visibility: seen?.lastVisibility.visibility ?? 0,
          contributions: { ...(seen?.lastVisibility.contributions ?? { angle: 0, distance: 0, exposure: 0, lineOfSight: 0 }) },
          lastDetection: detection === null ? null : { ...detection.contributions },
          fade: latch.fade,
        },
      };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      world.colliders.remove(gateCollider);
      world.colliders.remove(doorCollider);
      latch.dispose();
    },
  };
}

export const WEAKEST_LATCH_PART_MODULE: BeatPartModuleDefinition = {
  beat: 2,
  part: 1,
  room: 'farrowing',
  create: createWeakestLatchPartModule,
};
